import React, { useEffect, useState } from 'react';
import { adminService } from '../../services/adminService';
import Card, { CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Modal, { ModalHeader, ModalContent, ModalFooter } from '../../components/ui/Modal';
import FormInput from '../../components/ui/FormInput';

interface Coupon {
  id: string;
  code: string;
  description?: string;
  percentOff?: number;
  amountOff?: number;
  currency?: string;
  maxRedemptions?: number;
  redeemBy?: string;
  timesRedeemed: number;
  active: boolean;
  createdAt: string;
}

const AdminCouponsPage: React.FC = () => {
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState<Coupon | null>(null);
  const [formData, setFormData] = useState({
    code: '',
    description: '',
    percentOff: '',
    amountOff: '',
    currency: 'USD',
    maxRedemptions: '',
    redeemBy: '',
    active: true,
  });

  const load = async () => {
    setLoading(true);
    const res = await adminService.listCoupons();
    setCoupons(res.coupons || []);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const handleCreate = () => {
    setFormData({
      code: '',
      description: '',
      percentOff: '',
      amountOff: '',
      currency: 'USD',
      maxRedemptions: '',
      redeemBy: '',
      active: true,
    });
    setEditingCoupon(null);
    setShowCreateModal(true);
  };

  const handleEdit = (coupon: Coupon) => {
    setFormData({
      code: coupon.code,
      description: coupon.description || '',
      percentOff: coupon.percentOff?.toString() || '',
      amountOff: coupon.amountOff?.toString() || '',
      currency: coupon.currency || 'USD',
      maxRedemptions: coupon.maxRedemptions?.toString() || '',
      redeemBy: coupon.redeemBy ? new Date(coupon.redeemBy).toISOString().slice(0, 16) : '',
      active: coupon.active,
    });
    setEditingCoupon(coupon);
    setShowCreateModal(true);
  };

  const handleSubmit = async () => {
    try {
      const data: any = {
        code: formData.code,
        active: formData.active,
      };

      if (formData.description) data.description = formData.description;
      if (formData.percentOff) data.percentOff = parseFloat(formData.percentOff);
      if (formData.amountOff) {
        data.amountOff = parseFloat(formData.amountOff);
        data.currency = formData.currency;
      }
      if (formData.maxRedemptions) data.maxRedemptions = parseInt(formData.maxRedemptions);
      if (formData.redeemBy) data.redeemBy = new Date(formData.redeemBy).toISOString();

      if (editingCoupon) {
        await adminService.updateCoupon(editingCoupon.id, data);
      } else {
        await adminService.createCoupon(data);
      }

      setShowCreateModal(false);
      await load();
    } catch (error: any) {
      alert(error?.response?.data?.message || 'Failed to save coupon');
    }
  };

  const formatDiscount = (coupon: Coupon) => {
    if (coupon.percentOff) {
      return `${coupon.percentOff}% off`;
    } else if (coupon.amountOff) {
      return `${coupon.currency || 'USD'} ${coupon.amountOff} off`;
    }
    return 'N/A';
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold text-gray-900">Coupons</h1>
        <Button onClick={handleCreate}>Create Coupon</Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Discount</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Redemptions</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expires</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr><td className="px-4 py-6" colSpan={6}>Loading…</td></tr>
                ) : coupons.length === 0 ? (
                  <tr><td className="px-4 py-6" colSpan={6}>No coupons found.</td></tr>
                ) : coupons.map(c => (
                  <tr key={c.id}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{c.code}</div>
                      {c.description && <div className="text-sm text-gray-500">{c.description}</div>}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">{formatDiscount(c)}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {c.timesRedeemed}
                      {c.maxRedemptions && ` / ${c.maxRedemptions}`}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        c.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                      }`}>
                        {c.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {c.redeemBy ? new Date(c.redeemBy).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <Button size="sm" variant="outline" onClick={() => handleEdit(c)}>Edit</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Modal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)}>
        <ModalHeader>{editingCoupon ? 'Edit Coupon' : 'Create Coupon'}</ModalHeader>
        <ModalContent>
          <div className="space-y-4">
            <FormInput
              label="Code"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
              required
            />
            <FormInput
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
            <div className="grid grid-cols-2 gap-4">
              <FormInput
                label="Percent Off (%)"
                type="number"
                value={formData.percentOff}
                onChange={(e) => setFormData({ ...formData, percentOff: e.target.value, amountOff: '' })}
                min="1"
                max="100"
              />
              <div>
                <FormInput
                  label="Amount Off"
                  type="number"
                  value={formData.amountOff}
                  onChange={(e) => setFormData({ ...formData, amountOff: e.target.value, percentOff: '' })}
                  min="0.5"
                  step="0.01"
                />
                {formData.amountOff && (
                  <select
                    value={formData.currency}
                    onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                    className="mt-2 w-full px-3 py-2 border rounded"
                  >
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                  </select>
                )}
              </div>
            </div>
            <FormInput
              label="Max Redemptions"
              type="number"
              value={formData.maxRedemptions}
              onChange={(e) => setFormData({ ...formData, maxRedemptions: e.target.value })}
              min="1"
            />
            <FormInput
              label="Expires At"
              type="datetime-local"
              value={formData.redeemBy}
              onChange={(e) => setFormData({ ...formData, redeemBy: e.target.value })}
            />
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={formData.active}
                onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                className="mr-2"
              />
              <label className="text-sm text-gray-700">Active</label>
            </div>
          </div>
        </ModalContent>
        <ModalFooter>
          <Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
          <Button onClick={handleSubmit}>Save</Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default AdminCouponsPage;


